import { useTranslation } from 'react-i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { COMMUNITY_DIRECTORY_CACHE_PREFIX } from '~/constants/community';
import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ProfileFieldAnswer, ProfileFieldErrors } from '~/interfaces/community';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';

export class ProfileFieldsRequestError extends Error {
  fieldErrors: ProfileFieldErrors;

  constructor(message: string, fieldErrors: ProfileFieldErrors = {}) {
    super(message);
    this.name = 'ProfileFieldsRequestError';
    this.fieldErrors = fieldErrors;
  }
}

type UpdateProfileFieldsPayload = {
  answers: ProfileFieldAnswer[];
  pageBlock?: NectarPageBlock;
};

type UpdateProfileFieldsResponse = {
  error?: string;
  errors?: ProfileFieldErrors;
};

const isResponse = (value: unknown): value is UpdateProfileFieldsResponse => typeof value === 'object' && value != null;

const useUpdateProfileFieldsMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ answers, pageBlock }: UpdateProfileFieldsPayload): Promise<void> => {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch('/community_profile_fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify({ answers, ...withNectarTrackingParams({}, pageBlock) }),
      });

      if (!response.ok) {
        const data: unknown = await response.json().catch(() => null);
        const message = isResponse(data) && data.error ? data.error : t('community.profile_fields.save_error');
        throw new ProfileFieldsRequestError(message, isResponse(data) && data.errors ? data.errors : {});
      }
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: [COMMUNITY_DIRECTORY_CACHE_PREFIX] });
    },
  });
};

export default useUpdateProfileFieldsMutation;
